import type { Card } from '@uno/shared';

/**
 * A card theme describes how to paint a card face.
 *
 * Two flavours:
 *  - `css`  — drawn with HTML/CSS (the built-in default, zero assets)
 *  - `png`  — a folder of PNG images, one per card face
 *
 * PNG themes are registered in `public/themes/index.json`, so adding one is:
 *   1. drop the PNGs into `public/themes/<your-theme>/`
 *   2. add an entry to `public/themes/index.json`
 * No rebuild, no code change.
 */
export interface CardTheme {
  /** Unique id, also the folder name for PNG themes. */
  id: string;
  /** Shown in the theme picker. */
  name: string;
  kind: 'css' | 'png';
  /** Author credit, shown as a subtitle in the picker. */
  credit?: string;

  /**
   * Native pixel size of the artwork. Cards are rendered at the aspect ratio
   * derived from these, scaled to fit the layout. Standard is 300x450.
   */
  width: number;
  height: number;
  /** Corner radius as a percentage of card width, for masking PNG corners. */
  radiusPct?: number;

  /* --- PNG themes only ------------------------------------------- */

  /** Root the image paths are resolved against. Defaults to `/themes/<id>/`. */
  basePath?: string;
  /**
   * Filename template used when `faces` has no explicit entry.
   * `{key}` expands to the canonical card key: `red-7`, `blue-skip`, `wild4`, ...
   */
  pattern?: string;
  /** Per-card overrides, keyed by the same card key. */
  faces?: Record<string, string>;
  /** Card back image. Falls back to the CSS back when absent. */
  back?: string;
}

/** The shape of `public/themes/index.json`. */
export interface ThemeIndex {
  themes: CardTheme[];
}

/** Canonical face key for a card — `red-7`, `green-reverse`, `wild`, `wild4`. */
export function themeKey(card: Card): string {
  if (card.kind === 'wild') return 'wild';
  if (card.kind === 'wild4') return 'wild4';
  if (card.kind === 'number') return `${card.color}-${card.value}`;
  return `${card.color}-${card.kind}`;
}

/** Resolves the image URL for a card under a PNG theme. */
export function faceUrl(theme: CardTheme, card: Card): string | null {
  if (theme.kind !== 'png') return null;
  const key = themeKey(card);
  const base = theme.basePath ?? `/themes/${theme.id}/`;
  const file = theme.faces?.[key] ?? (theme.pattern ?? '{key}.png').replace('{key}', key);
  return new URL(file, new URL(base, window.location.origin)).pathname;
}

export function backUrl(theme: CardTheme): string | null {
  if (theme.kind !== 'png' || !theme.back) return null;
  const base = theme.basePath ?? `/themes/${theme.id}/`;
  return new URL(theme.back, new URL(base, window.location.origin)).pathname;
}
